import { Favourite } from "@/icons";
import clsx from "clsx";
import React, { CSSProperties, useMemo } from "react";
import { PropsWithChildren, ReactNode } from "react";
import { InfoPopup, InfoPopupProps } from "../InfoPopup";
import "./ActionIconText.scss";
import { ColorKeys } from "@theme/colors.ts";
import { Row } from "@/components";

export type ActionIconTextProps = PropsWithChildren<{
  icon?: ReactNode;
  noIcon?: boolean;
  textLabel?: string;
  handleAction?: () => void;
  handleIconAction?: () => void;
  classNameComponent?: string;
  classNameText?: string;
  disablePointer?: boolean;
  iconPosition?: "left" | "right";
  popupInfo?: InfoPopupProps;
  iconColor?: ColorKeys;
  positionPopUp?: "right" | "top" | "bottom" | "left" | undefined;
  justifyContent?: CSSProperties["justifyContent"];
  alignItems?: CSSProperties["alignItems"];
  children?: ReactNode;
}>;

export const ActionIconText = ({
  iconColor,
  icon = <Favourite color={iconColor} />,
  noIcon = false,
  textLabel = "Default text",
  handleAction = () => {},
  handleIconAction,
  classNameComponent = "fs-7",
  classNameText = "",
  disablePointer = false,
  iconPosition = "left",
  popupInfo,
  positionPopUp = "right",
  justifyContent = "center",
  alignItems = "center",
  children = undefined,
}: ActionIconTextProps) => {
  const runIconAction = (e: React.MouseEvent) => {
    if (!disablePointer) {
      // Se handleIconAction è definito, usa quello, altrimenti non fare stopPropagation
      // così il click si propaga al container principale
      if (handleIconAction) {
        e.stopPropagation();
        if (popupInfo) {
          setPopupIsOpen(!popupIsOpen);
        }
        handleIconAction();
      }
      // Se handleIconAction non è definito, il click si propaga naturalmente a handleAction
    }
  };

  const [popupIsOpen, setPopupIsOpen] = React.useState(false);

  const iconToRender = useMemo(() => {
    return icon || <Favourite color={iconColor} />;
  }, [iconColor, icon]);

  return (
    <Row
      justifyContent={justifyContent}
      alignItems={alignItems}
      gap={"x8"}
      className={clsx(
        "position-relative",
        disablePointer ? "text-muted" : "cursor-pointer text-primary",
        classNameComponent,
      )}
      onClick={() => handleAction && !disablePointer && handleAction()}
    >
      {!noIcon &&
      (iconPosition === "left" || iconPosition == null) &&
      !popupInfo ? (
        <Row alignItems={"center"} onClick={(e) => runIconAction(e)}>
          {iconToRender}
        </Row>
      ) : (
        popupInfo &&
        !noIcon &&
        (iconPosition === "left" || iconPosition == null) && (
          <InfoPopup
            title={popupInfo.title}
            description={popupInfo.description}
            isOpen={popupIsOpen}
            positionPopUp={positionPopUp}
            onClickCloseBtn={() => setPopupIsOpen(!popupIsOpen)}
            className={popupInfo.className}
            ariaLabel={popupInfo.ariaLabel}
            width={popupInfo.width}
          >
            {popupInfo.children}
          </InfoPopup>
        )
      )}
      <p className={clsx("fw-bold", classNameText)}>{textLabel}</p>

      {!noIcon && iconPosition === "right" && !popupInfo ? (
        <Row
          gap={"x24"}
          alignItems={"center"}
          onClick={(e) => runIconAction(e)}
        >
          {children}
          {icon}
        </Row>
      ) : (
        popupInfo &&
        !noIcon &&
        iconPosition === "right" && (
          <InfoPopup
            title={popupInfo.title}
            description={popupInfo.description}
            isOpen={popupIsOpen}
            positionPopUp={positionPopUp}
            onClickCloseBtn={() => setPopupIsOpen(!popupIsOpen)}
            className={popupInfo.className}
            ariaLabel={popupInfo.ariaLabel}
          ></InfoPopup>
        )
      )}
      {noIcon && children && (
        <Row
          gap={"x24"}
          alignItems={"center"}
          onClick={(e) => runIconAction(e)}
        >
          {children}
        </Row>
      )}
    </Row>
  );
};
